import {jsx, ParamComponent} from 'panel';
import last from 'lodash/last';
import isEmpty from 'lodash/isEmpty';

import {FilterValueProps} from 'common/query-builder/query-entry-section/row-types/filter/types';
import {serializeAttributes} from 'common/util';
import {qbStyleAttrs} from 'common/query-builder/query-builder/util';
import {preventTracking} from 'common/util/dom';
import {QueryBlockEl} from 'common/query-builder/query-block';
import {MenuType} from 'common/query-builder/query-block/types';
import {ButtonSize, ButtonTheme, ButtonType} from 'common/components/button/constants';
import {Placement} from 'common/components/tooltip/types';
import {ButtonEl} from 'common/components/button';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {SvgIconEl} from 'common/components/svg-icon';
import {getIconForPropertyType} from 'common/report/util/properties';
// eslint-disable-next-line import/no-cycle -- filterparts can render list of objects value component
import {FilterParts} from 'common/query-builder/query-entry-section/row-types/filter/parts';
import {SelectPropertyEvent} from 'common/query-builder/query-block/properties-menu/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {PropertyType, ResourceType} from 'common/report/constants';
import {ControlBarEl} from 'common/widgets/control-bar';
import {
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
} from 'common/query-builder/query-entry-section/constants';
import {getPropertyType, setPropertyType} from 'common/query-builder/query-entry-section/row-types/row-util';
import {isComputedListPropertyFilter} from 'common/widgets/property-filter-menu/utils';

enum Part {
  SubFilterProperty = `subFilterProperty`,
  SubFilterValue = `subFilterValue`,
}

interface Props {
  params: FilterValueProps;
}

interface State {
  /** The subproperty row and part that's currently open (empty object if none) */
  open: {index: number; part: Part} | EmptyObj;
}

class FilterListObjectValue extends ParamComponent<FilterValueProps, State, IronContextRegistry> {
  connectedCallback() {
    super.connectedCallback();

    // eager-open the menu when it's first added
    const firstListItemFilter = this.params.filter?.listItemFilters?.[0];
    if (firstListItemFilter) {
      if (!firstListItemFilter.propertyName) {
        this.openMenu(0, Part.SubFilterProperty);
      } else if (isEmpty(firstListItemFilter.filterValue)) {
        // necessary because this component is re-rendered after selecting the first sub-value
        this.openMenu(0, Part.SubFilterValue);
      }
    }
  }
  get config() {
    return {
      params: {
        filter: Object,
        isActive: Boolean,
        onChange: Function,
        updateEntry: Function,
        setActiveEntry: Function,
        onSelectMenuChange: Function,
        eventNames: Array,
        dataGroupId: String,
        projectId: Number,
        workspaceId: Number,
        userId: Number,
        readonly: Boolean,
        hasNewUI: Boolean,
        rowActions: Object,
      },
      defaultState: {
        open: {},
      },
      hooks: {
        preUpdate: (_newState, newParams?: FilterValueProps) => {
          if (
            newParams &&
            newParams.filter?.listItemFilters?.length > (this.params.filter?.listItemFilters?.length || 0)
          ) {
            // a new entry was added to listItemFilters, eager-open its value menu
            this.openMenu(newParams.filter.listItemFilters.length - 1, Part.SubFilterProperty);
          }
        },
      },
      // eslint-disable-next-line sonarjs/cognitive-complexity
      template: () => {
        const props = this.params;

        return (
          <div sel='.mp-filter-parts-list-objects'>
            {props.filter.listItemFilters.map((filter, idx) => {
              const hasProperty = filter.hasSelectedProperty();
              const rowIcon = getIconForPropertyType(filter.propertyType);
              const canDelete = props.filter.listItemFilters.length > 1;
              const propertyType = getPropertyType(filter);
              const subFilterPropertyOpen =
                props.isActive && this.state.open.index === idx && this.state.open.part === Part.SubFilterProperty;
              const subFilterValueOpen =
                props.isActive && this.state.open.index === idx && this.state.open.part === Part.SubFilterValue;

              const updateListItemFilters = (newFilters: PropertyFilter[]) => {
                props.updateEntry(props.filter.setAttribute(`listItemFilters`, newFilters));
              };

              const updateFilter = (newFilter: PropertyFilter) => {
                updateListItemFilters(
                  props.filter.listItemFilters.map((filter, i) => (i === idx ? newFilter : filter)),
                );
              };

              const onPropertySelected = (index: number, ev: SelectPropertyEvent) => {
                const selection = ev.detail.item as Partial<PropertyFilter>;
                const sameType = filter.propertyType === selection.propertyType;
                let newFilter = new PropertyFilter({
                  ...selection,
                  // preserve filter operator and value if same type
                  filterOperator: sameType ? filter.filterOperator : undefined,
                  filterValue: sameType ? filter.filterValue : undefined,
                });

                // auto cast any lists to string. UI doesn't currently support more than 1 depth list
                if (newFilter.listQuantifier) {
                  newFilter = setPropertyType(
                    newFilter,
                    {
                      type: PropertyType.String,
                      isList: false,
                    },
                    true,
                  );
                }

                // when selecting a computed property, we want to replace all other existing filters
                if (isComputedListPropertyFilter(newFilter)) {
                  updateListItemFilters([newFilter]);
                } else {
                  updateFilter(newFilter);
                }

                if (!sameType && newFilter.propertyType !== PropertyType.Datetime) {
                  this.openMenu(index, Part.SubFilterValue);
                }
              };

              const onClickDelete = () => {
                updateListItemFilters(props.filter.listItemFilters.filter((_, i) => i !== idx));
              };

              // when this component is displayed in the old QB layout, we hide the first
              // row's action buttons so that the parent mp-query-entry-section controls
              // this row's action buttons
              const shouldRenderRowActions = !props.hasNewUI && idx === 0;

              const overflowControls = hasProperty && isComputedListPropertyFilter(filter) === false && (
                <ControlBarEl
                  key='entry-actions'
                  attrs={{
                    elref: `lineActions`,
                    theme: ButtonTheme.Tertiary,
                    'overflow-icon': props.hasNewUI ? `ellipsis` : `hor-ellipsis`,
                    'overflow-controls': [
                      {
                        items: [
                          ControlBarActionItems[ControlBarAction.Typecast](propertyType, false),
                          ControlBarActionItems[ControlBarAction.Duplicate],
                          canDelete && ControlBarActionItems[ControlBarAction.Delete],
                        ].filter(Boolean),
                      },
                    ],
                    'overflow-menu-width': 200,
                    'overflow-menu-height': 330,
                    disabled: props.readonly,
                  }}
                  on={{
                    select: (ev: CustomEvent) => {
                      const value = ev.detail.value as ControlBarActionValue;

                      switch (value.action) {
                        case ControlBarAction.Typecast:
                          updateFilter(setPropertyType(filter, value.toType, true));
                          break;
                        case ControlBarAction.Duplicate:
                          updateListItemFilters([...props.filter.listItemFilters, filter.clonePropertyFilter({})]);
                          break;
                        case ControlBarAction.Delete:
                          onClickDelete();
                          break;
                      }
                    },
                  }}
                />
              );

              const actions = shouldRenderRowActions ? (
                props.rowActions
              ) : (
                <div sel='.actions'>
                  {!props.hasNewUI && overflowControls}
                  {canDelete && (
                    <ButtonEl
                      attrs={{
                        elref: `deleteItemFilterButton`,
                        theme: ButtonTheme.Tertiary,
                        icon: props.hasNewUI ? `x` : `trashcan`,
                        role: `button`,
                        type: ButtonType.Destructive,
                        tooltip: {
                          text: `Remove`,
                          placement: Placement.Top,
                        },
                      }}
                      on={{
                        click: onClickDelete,
                      }}
                    />
                  )}
                  {props.hasNewUI && overflowControls}
                </div>
              );

              return (
                <div sel='.mp-filter-parts-list-object'>
                  {!props.hasNewUI && idx > 0 && (
                    <div sel='.entry-selector-prefix'>
                      <SelectEl
                        class={{'is-disabled': true}}
                        attrs={{
                          readonly: true,
                          elref: `filtersOperatorSelect`,
                          theme: SelectTheme.Tertiary,
                          'menu-width': 90,
                          size: ButtonSize.Medium,
                          sections: [],
                          'selected-option': `and`,
                        }}
                      />
                    </div>
                  )}
                  <div sel='.mp-filter-parts-list-object-property'>
                    {props.hasNewUI && rowIcon && <SvgIconEl attrs={{icon: rowIcon}} />}
                    <QueryBlockEl
                      class={{
                        'is-placeholder': !hasProperty,
                      }}
                      attrs={serializeAttributes({
                        type: MenuType.Filter,
                        'event-names': props.eventNames,
                        'disable-value-selection': true,
                        open: subFilterPropertyOpen,
                        'data-group-id': props.dataGroupId,
                        'project-has-cohorts': false,
                        'project-id': props.projectId,
                        selected: hasProperty ? filter.getAttributes() : null,
                        theme: qbStyleAttrs(props.hasNewUI).selectorTheme,
                        'selector-type': `button`,
                        'selector-icon': ``,
                        'user-id': props.userId,
                        'workspace-id': props.workspaceId,
                        'hide-events': props.filter.resourceType === ResourceType.User,
                      })}
                      params={{
                        listProperty: last(filter.getParents()),
                      }}
                      on={{
                        select: (ev: SelectPropertyEvent) => {
                          onPropertySelected(idx, ev);
                        },
                        track: preventTracking,
                        open: () => {
                          this.openMenu(idx, Part.SubFilterProperty);
                        },
                        close: () => {
                          if (this.state.open.part === Part.SubFilterProperty) {
                            // only reset if open part hasn't changed (on select, it changes to Part.SubFilterValue)
                            this.closeMenu();
                          }
                        },
                      }}
                    />
                    {props.hasNewUI && actions}
                  </div>
                  {hasProperty && (
                    <FilterParts
                      filter={filter}
                      hasNewUI={props.hasNewUI}
                      projectId={props.projectId}
                      workspaceId={props.workspaceId}
                      userId={props.userId}
                      dataGroupId={props.dataGroupId}
                      eventNames={props.eventNames}
                      readonly={props.readonly}
                      updateEntry={(filter) => updateFilter(filter)}
                      isValueActive={subFilterValueOpen}
                      onValueSelectMenuChange={(ev) =>
                        this.onChangeFilterValue(idx, ev.detail.isOpen ? `open` : `close`)
                      }
                      setActiveEntry={(state) => this.onChangeFilterValue(idx, state)}
                      showTypecastControl
                    />
                  )}
                  {!props.hasNewUI && actions}
                </div>
              );
            })}
          </div>
        );
      },
    };
  }

  /** Handles active entry events from a subproperty value. */
  onChangeFilterValue(index: number, state: `open` | `close`) {
    if (state === `open`) {
      this.openMenu(index, Part.SubFilterValue);
    } else {
      this.closeMenu();
    }
  }

  openMenu(index: number, part: Part) {
    this.update({open: {index, part}});
    this.params.setActiveEntry(`open`);
  }

  closeMenu() {
    this.update({open: {}});
    this.params.setActiveEntry(`close`);
  }
}

export const FilterListObjectValueEl = defineAndMakeJsxFactory<Props>(
  `mp-filter-list-object-value`,
  FilterListObjectValue,
);
export const FilterListObjectValueReact = panel2react<Props>(`mp-filter-list-object-value`, FilterListObjectValue);
